import {
  Box,
  VStack,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Input,
  Popover,
  useDisclosure,
  PopoverTrigger,
  PopoverContent,
  CheckboxGroup,
  Checkbox,
  IconButton,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import {
  DOPPEL_BORDER_GREY,
  DOPPEL_BUTTON_GREY,
  DOPPEL_BUTTON_GREY_SHADE,
  DOPPEL_CYAN_200,
  DOPPEL_GREY,
  DOPPEL_TEXT_BLACK,
  DOPPEL_TEXT_WHITE,
  DOPPEL_WHITE,
  FONT_SIZE_SMALL,
  ICON_SIZE,
} from '@/utils/style'
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import { useSelectedReportFilters } from '@/pages/reports'
import { removeFilterByType, ReportFilter } from '@/hooks/report_table_filters'
import { ReportFilterType } from '@/utils/constants'
import { upsertFilterListForType } from '@/hooks/base_filters'
import { MdPassword } from 'react-icons/md'
import { IoMdClose } from 'react-icons/io'
import DropdownButton from '@/components/doppel_design/dropdown_button'
import { PasswordComplexity } from '@/generated/enums'
import safeRegex from 'safe-regex'

type PasswordFilterDropdownProps = {
  onClear?: () => void
  onSelect?: (value: string[]) => void
  margin?: number
}

const PasswordFilterDropdown = ({
  onClear,
  onSelect,
  margin = 2,
}: PasswordFilterDropdownProps) => {
  const [selectedOptions, setSelectedOptions] = useState([])
  const [minLength, setMinLength] = useState(1)
  const [customRegex, setCustomRegex] = useState('')
  const { isOpen, onOpen, onToggle, onClose } = useDisclosure()
  const fontSize = '14px'
  const [selectedReportFilters, setSelectedReportFilters] = useSelectedReportFilters()
  const filterType = ReportFilterType.PasswordComplexity
  const dropdownPosition = 'bottom-start'
  const checkboxOptions = [
    {
      label: 'Requires Uppercase Letter',
      value: PasswordComplexity.REQUIRES_UPPERCASE_LETTER,
    },
    {
      label: 'Requires Number',
      value: PasswordComplexity.REQUIRES_NUMBER,
    },
    {
      label: 'Requires Special Characters',
      value: PasswordComplexity.REQUIRES_SPECIAL_CHARACTERS,
    },
  ]
  const passwordComplexityFilters = selectedReportFilters.filter(
    (filter: ReportFilter) => filter.filterType === filterType,
  )
  const isAnyOptionSelected = passwordComplexityFilters.length > 0

  // Set initial filter values
  const handleClear = () => {
    onClear && onClear()
    setSelectedOptions([])
    setMinLength(1)
    setCustomRegex('')
    setSelectedReportFilters(removeFilterByType(filterType, selectedReportFilters))
    // close the Popover if it was open
    onClose()
  }
  useEffect(() => {
    if (passwordComplexityFilters) {
      passwordComplexityFilters.forEach((filter: ReportFilter) => {
        if (filter.value.includes(PasswordComplexity.PASSWORD_LENGTH)) {
          const length = filter.value.split(`${PasswordComplexity.PASSWORD_LENGTH}_`)[1]
          setMinLength(Number(length))
        } else if (filter.value.includes(PasswordComplexity.CUSTOM_REGEX)) {
          const regex = filter.value.split(`${PasswordComplexity.CUSTOM_REGEX}_`)[1]
          if (safeRegex(regex)) {
            setCustomRegex(regex)
          }
        } else {
          setSelectedOptions((prev) => [...prev, filter.value])
        }
      })
    }
  }, []) // Empty dependency array ensures this only runs once on mount

  useEffect(() => {
    const values = [...selectedOptions]

    if (minLength > 1) {
      values.push(`${PasswordComplexity.PASSWORD_LENGTH}_${minLength}`)
    }

    if (customRegex && safeRegex(customRegex)) {
      values.push(`${PasswordComplexity.CUSTOM_REGEX}_${customRegex}`)
    }

    setSelectedReportFilters(
      upsertFilterListForType(filterType, values, selectedReportFilters),
    )
    onSelect?.(values)
  }, [selectedOptions, customRegex, minLength])

  let buttonColors = {
    text: DOPPEL_TEXT_WHITE,
    bg: DOPPEL_BUTTON_GREY,
    bgHover: DOPPEL_BUTTON_GREY_SHADE,
  }

  if (isAnyOptionSelected) {
    buttonColors = {
      text: DOPPEL_TEXT_WHITE,
      bg: DOPPEL_BUTTON_GREY_SHADE,
      bgHover: DOPPEL_BUTTON_GREY_SHADE,
    }
  }

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      placement={dropdownPosition}
    >
      <PopoverTrigger>
        <Box mr={margin}>
          <DropdownButton
            bgColor={buttonColors.bg}
            border={`0.5px solid ${DOPPEL_BORDER_GREY}`}
            color={buttonColors.text}
            fontSize={FONT_SIZE_SMALL}
            h={8}
            icon={<MdPassword size={ICON_SIZE} />}
            label={'Password Complexity'}
            minWidth="50px"
            onClick={onToggle}
            rightIcon={
              isAnyOptionSelected ? (
                <IconButton
                  _hover={{
                    background: 'transparent',
                  }}
                  aria-label="Clear selection"
                  as={Box}
                  bgColor="transparent"
                  color={DOPPEL_WHITE}
                  icon={<IoMdClose size={ICON_SIZE} />}
                  isRound={true}
                  onClick={(e) => {
                    // stop this click from toggling the Popover
                    e.stopPropagation()
                    handleClear()
                  }}
                  size={`${ICON_SIZE + 10}px`}
                />
              ) : (
                <IconButton
                  _hover={{
                    background: 'transparent',
                  }}
                  aria-label="Open dropdown"
                  as={Box}
                  bgColor="transparent"
                  color={DOPPEL_WHITE}
                  icon={<ChevronDownIcon h={4} w={4} />}
                  isRound={true}
                  size="14px"
                />
              )
            }
          />
        </Box>
      </PopoverTrigger>

      <PopoverContent width="fit-content">
        <Box
          bg="white"
          borderRadius="md"
          borderWidth="1px"
          boxShadow="md"
          maxW="250px"
          p={4}
        >
          <VStack align="start" spacing={4} textColor={DOPPEL_TEXT_BLACK}>
            <VStack align="start" spacing={3}>
              <CheckboxGroup onChange={setSelectedOptions} value={selectedOptions}>
                {checkboxOptions.map((option) => (
                  <Checkbox
                    key={option.value}
                    sx={{
                      '.chakra-checkbox__control': {
                        border: '2px solid',
                        borderColor: DOPPEL_TEXT_BLACK,
                        _checked: {
                          bg: DOPPEL_CYAN_200,
                          borderColor: DOPPEL_CYAN_200,
                        },
                        _focusVisible: {
                          boxShadow: 'none',
                          outline: 'none',
                        },
                      },
                      '.chakra-checkbox__label': {
                        fontSize: fontSize,
                      },
                    }}
                    value={option.value}
                  >
                    {option.label}
                  </Checkbox>
                ))}
              </CheckboxGroup>
            </VStack>

            <VStack align="start" spacing={2} w="100%">
              <Text fontSize={fontSize}>Minimum Password Length</Text>

              <NumberInput
                focusBorderColor={DOPPEL_CYAN_200}
                max={128}
                min={1}
                onChange={(v) => {
                  setMinLength(Number(v))
                }}
                value={minLength}
              >
                <NumberInputField
                  _hover={{ borderColor: DOPPEL_CYAN_200 }}
                  borderColor={DOPPEL_GREY}
                  fontSize={fontSize}
                />

                <NumberInputStepper>
                  <NumberIncrementStepper>
                    <ChevronUpIcon boxSize={4} color={DOPPEL_TEXT_BLACK} />
                  </NumberIncrementStepper>

                  <NumberDecrementStepper>
                    <ChevronDownIcon boxSize={4} color={DOPPEL_TEXT_BLACK} />
                  </NumberDecrementStepper>
                </NumberInputStepper>
              </NumberInput>
            </VStack>

            <VStack align="start" spacing={2} w="100%">
              <Text fontSize={fontSize}>Custom Regex</Text>

              <Input
                _hover={{ borderColor: DOPPEL_CYAN_200 }}
                borderColor={DOPPEL_GREY}
                focusBorderColor={DOPPEL_CYAN_200}
                fontSize={fontSize}
                maxLength={200}
                onChange={(e) => {
                  setCustomRegex(e.target.value)
                }}
                placeholder="Text"
                value={customRegex}
              />
            </VStack>
          </VStack>
        </Box>
      </PopoverContent>
    </Popover>
  )
}

export default PasswordFilterDropdown
